import {
  Alert,
  Button,
  Input,
  NumericInput,
  SummaryCollapse,
  Tooltip,
  Typography,
} from "@kyan/ui";
import { AnimatePresence, motion } from "motion/react";
import { Address } from "viem";

import { AccountSelector } from "#/components/AccountSelector/AccountSelector";
import { ConnectWalletWrapper } from "#/components/ConnectWalletWrapper/ConnectWalletWrapper";
import { isMainnet } from "#/config/web3/contractAddresses";
import { RequestStatusEnum } from "#/services/bridge/bridge.types";
import { MAX_NET_DEPOSIT } from "#/services/kyan/hooks/useNetDeposits";
import { formatCurrency } from "#/utils/valueFormatters";

import { FormInfo } from "../FormInfo";
import { MarketSelector } from "../MarketSelector";
import { Summary } from "../Summary";
import { BridgeButton } from "./components/BridgeButton/BridgeButton";
import { BridgeHistory } from "./components/BridgeHistory/BridgeHistory";
import { BridgeId } from "./components/BridgeId/BridgeId";
import { EquityChange } from "./components/EquityChange/EquityChange";
import { MintButton } from "./components/MintButton/MintButton";
import { QuoteInfo } from "./components/QuoteInfo/QuoteInfo";
import { TokenSelector } from "./components/TokenSelector/TokenSelector";
import { useDepositFormController } from "./DepositForm.controller";

interface DepositFormProps {
  isInitialDeposit?: boolean;
}

export const DepositForm: React.FC<DepositFormProps> = ({
  isInitialDeposit,
}) => {
  const {
    isBridgeNeeded,
    handleSubmit,
    ctaLabel,
    tooltip,
    isFormValid,
    isDisabled,
    isLoading,
    account,
    setAccount,
    amount,
    equityModalPortfolio,
    setEquityModalPortfolio,
    setDepositAmount,
    portfolioState,
    errorMessage,
    warningMessage,
    finalAmount,
    isDevMode,
    targetAccount,
    setTargetAccount,
    setInputToken,
    inputToken,
    setAmount,
    minValue,
    maxValue,
    bridgeId,
    bridgeStatus,
    quote,
    showBridgeHistory,
  } = useDepositFormController();

  if (showBridgeHistory) {
    return <BridgeHistory />;
  }

  return (
    <div className="relative flex w-full flex-col items-center">
      <div className="flex w-full items-center justify-between pr-6">
        <Typography
          medium
          grotesk
          primary
          element="h2"
          className="w-full text-left"
        >
          Deposit funds
        </Typography>
        {!isMainnet && <MintButton />}
      </div>

      <BridgeButton />

      {Boolean(isInitialDeposit && minValue) && (
        <Typography className="mt-5 w-full text-left">
          To begin trading on Kyan, you need to have at least ${minValue}
        </Typography>
      )}

      <div className="flex w-full flex-col">
        <FormInfo title="Token" />
        <TokenSelector
          token={inputToken}
          onChange={(token) => {
            setInputToken(token);
            setAmount("");
          }}
        />

        <NumericInput
          label="Amount"
          value={amount}
          error={errorMessage}
          maxButtonValue={maxValue}
          minButtonValue={minValue}
          rightLabelClassName="mt-auto"
          labelClassName="text-textSecondary mt-4"
          autoNumericOptions={{
            decimalPlaces: inputToken.decimals,
          }}
          rightLabel={
            isMainnet
              ? `Current max net deposits: ${formatCurrency(MAX_NET_DEPOSIT, { precision: 0 })}`
              : undefined
          }
          onChange={setDepositAmount}
        />

        <AnimatePresence mode="wait">
          {quote?.autoRoute && (
            <motion.div
              exit={{ opacity: 0, height: 0 }}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{
                duration: 0.15,
                ease: "easeInOut",
              }}
            >
              <QuoteInfo quote={quote} />
            </motion.div>
          )}
        </AnimatePresence>

        {warningMessage && (
          <Alert
            className="mt-4"
            variant="warning"
            title={warningMessage}
            titleClassName="font-medium"
          />
        )}

        <FormInfo
          title="To"
          info={
            <EquityChange
              finalAmount={finalAmount}
              equity={portfolioState.equity}
            />
          }
        />

        <AccountSelector account={account} setAccount={setAccount} />
        {isDevMode && (
          <Input
            placeholder="0x..."
            value={targetAccount}
            inputWrapperClassName="my-2"
            onChange={(e) => {
              setTargetAccount(e.target.value as Address);
            }}
          />
        )}

        {!isBridgeNeeded && (
          <>
            <MarketSelector
              title="portfolio"
              value={equityModalPortfolio}
              onChange={setEquityModalPortfolio}
            />
            {account && isFormValid && (
              <SummaryCollapse title="Summary" className="mt-2 h-full">
                <Summary
                  account={account}
                  market={equityModalPortfolio}
                  amount={finalAmount.toString()}
                />
              </SummaryCollapse>
            )}
          </>
        )}

        <ConnectWalletWrapper className="mt-4" chainId={inputToken.chainId}>
          <Tooltip
            avoidCollisions
            side="top"
            align="center"
            className="z-10"
            disabled={!tooltip}
            content={<Typography size={12}>{tooltip}</Typography>}
          >
            <Button
              size="lg"
              className="mt-4"
              disabled={isDisabled}
              isLoading={isLoading}
              data-testid="deposit-funds-button"
              onClick={handleSubmit}
            >
              {ctaLabel}
            </Button>
          </Tooltip>
        </ConnectWalletWrapper>

        {Boolean(isBridgeNeeded && bridgeId) && (
          <BridgeId
            className="mt-4"
            bridgeId={bridgeId}
            status={bridgeStatus || RequestStatusEnum.PENDING}
          />
        )}
      </div>
    </div>
  );
};
